import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import styled, { css } from 'styled-components';

type DropdownMenuContentProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  variant?: 'default' | 'subtle';
};

const subtleStyles = css`
  border-radius: 8px;
  min-width: 240px;
`;

const RootStyled = styled(DropdownMenuPrimitive.Content)<{ $variant: NonNullable<DropdownMenuContentProps['variant']> }>`
  background: ${({ theme }) => theme.colors.neutral.background};
  border-radius: 4px;
  ${({ theme }) => theme.shadow.primary[8]};
  padding: 8px 0;
  transform-origin: var(--radix-dropdown-menu-content-transform-origin);
  will-change: transform, opacity;
  max-height: var(--radix-dropdown-menu-content-available-height);
  overflow-y: auto;

  ${({ $variant }) => $variant === 'subtle' && subtleStyles}

  @keyframes dropdownMenuIn {
    from {
      opacity: 0;
      transform: translateY(-2px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes dropdownMenuOut {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(-2px) scale(0.98);
    }
  }

  &[data-state='open'] {
    animation: dropdownMenuIn 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-state='closed'] {
    animation: dropdownMenuOut 120ms ease-in;
  }
`;

export const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  variant = 'default',
  ...props
}: DropdownMenuContentProps): React.JSX.Element => {
  return (
    <DropdownMenuPrimitive.Portal>
      <RootStyled
        data-slot="dropdown-menu-content"
        data-variant={variant}
        $variant={variant}
        sideOffset={sideOffset}
        className={className}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
};
